import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import apiConfig from '../config/api'
import styles from './Faqs.module.css'
import Header from '../components/Header'

const Faqs = () => {
  const [faqs, setFaqs] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newFaq, setNewFaq] = useState({
    question: '',
    answer: '',
    category: ''
  })
  const [newCategory, setNewCategory] = useState('')
  const [success, setSuccess] = useState(null)
  const [editingFaq, setEditingFaq] = useState(null)
  const [editFormData, setEditFormData] = useState({
    question: '',
    answer: '',
    category: ''
  })
  
  const { user } = useAuth()
  const isAdmin = user && user.role === 'admin'
  
  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await fetch(`${apiConfig.baseUrl}/api/faqs`)
        const data = await response.json()
        setFaqs(data)
        setLoading(false)
      } catch (err) {
        setError('Failed to load FAQs')
        setLoading(false)
      }
    }
    
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${apiConfig.baseUrl}/api/faqs/categories`)
        const data = await response.json()
        setCategories(data)
      } catch (err) {
        console.error('Failed to load categories', err)
      }
    }
    
    fetchFaqs()
    fetchCategories()
  }, [])
  
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setNewFaq(prev => ({ ...prev, [name]: value }))
  }

  const handleEditInputChange = (e) => {
    const { name, value } = e.target
    setEditFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleCategoryChange = (e) => {
    const value = e.target.value
    
    if (value === 'new') {
      setNewFaq(prev => ({ ...prev, category: 'new' }))
      setNewCategory('')
    } else {
      setNewFaq(prev => ({ ...prev, category: value }))
    }
  }

  const handleEditCategoryChange = (e) => {
    const value = e.target.value
    setEditFormData(prev => ({ ...prev, category: value }))
  }

  const handleNewCategoryChange = (e) => {
    setNewCategory(e.target.value)
  }
  
  const handleAddFaq = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    
    try {
      // Use the new category input if "Add New Category" is selected
      const faqData = {
        ...newFaq,
        category: newFaq.category === 'new' ? newCategory : newFaq.category
      }
      
      // Validate that a new category has been entered if "new" is selected
      if (faqData.category === 'new' || !faqData.category) {
        throw new Error('Please enter a valid category')
      }
      
      const response = await fetch(`${apiConfig.baseUrl}/api/faqs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(faqData)
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to add FAQ')
      }
      
      const addedFaq = await response.json()
      setFaqs(prev => [...prev, addedFaq])
      setNewFaq({ question: '', answer: '', category: '' })
      setNewCategory('')
      setSuccess('FAQ added successfully')
      setShowAddForm(false)
      
      // If it's a new category, add it to the list
      if (!categories.includes(addedFaq.category)) {
        setCategories(prev => [...prev, addedFaq.category])
      }
    } catch (err) {
      setError(err.message)
    }
  }
  
  const startEditingFaq = (faq) => {
    setEditingFaq(faq._id)
    setEditFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category
    })
    setError(null)
    setSuccess(null)
  }
  
  const cancelEditing = () => {
    setEditingFaq(null)
    setEditFormData({
      question: '',
      answer: '',
      category: ''
    })
  }
  
  const handleEditFaq = async (e, id) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    
    try {
      const response = await fetch(`${apiConfig.baseUrl}/api/faqs/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editFormData)
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to update FAQ')
      }
      
      const updatedFaq = await response.json()
      
      // Update the FAQ in the state
      setFaqs(prev => 
        prev.map(faq => 
          faq._id === id ? updatedFaq : faq
        )
      )
      
      setSuccess('FAQ updated successfully')
      setEditingFaq(null)
      
      // If it's a new category, add it to the list
      if (!categories.includes(updatedFaq.category)) {
        setCategories(prev => [...prev, updatedFaq.category])
      }
    } catch (err) {
      setError(err.message)
    }
  }

  const toggleForm = () => {
    setShowAddForm(prev => !prev)
    setError(null)
    setSuccess(null)
  }
  
  // Group FAQs by category
  const faqsByCategory = faqs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = []
    }
    acc[faq.category].push(faq)
    return acc
  }, {})
  
  const [expandedFaqs, setExpandedFaqs] = useState({})
  
  const toggleFaq = (id) => {
    if (editingFaq) return // Don't toggle when editing
    
    setExpandedFaqs(prev => ({
      ...prev,
      [id]: !prev[id]
    }))
  }

  if (loading) return (
    <>
      <Header />
      <div className={styles.faqsContainer}>
        <div className={styles.loading}>Loading FAQs...</div>
      </div>
    </>
  )
  
  return (
    <>
      <Header />
      <div className={styles.faqsContainer}>
        <h1 className={styles.pageTitle}>Frequently Asked Questions</h1>
        
        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>{success}</div>}
        
        {isAdmin && (
          <div className={styles.adminControls}>
            <button 
              onClick={toggleForm} 
              className={styles.addButton}
            >
              {showAddForm ? 'Cancel' : 'Add New FAQ'}
            </button>
            
            {showAddForm && (
              <form onSubmit={handleAddFaq} className={styles.addForm}>
                <div className={styles.formGroup}>
                  <label htmlFor="question">Question:</label>
                  <input
                    type="text"
                    id="question"
                    name="question"
                    value={newFaq.question}
                    onChange={handleInputChange}
                    required
                    className={styles.input}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="answer">Answer:</label>
                  <textarea
                    id="answer"
                    name="answer"
                    value={newFaq.answer}
                    onChange={handleInputChange}
                    required
                    className={styles.textarea}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="category">Category:</label>
                  <select
                    id="category"
                    name="category"
                    value={newFaq.category}
                    onChange={handleCategoryChange}
                    required
                    className={styles.select}
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                    <option value="new">Add New Category</option>
                  </select>
                  
                  {newFaq.category === 'new' && (
                    <div className={styles.newCategoryInput}>
                      <input
                        type="text"
                        placeholder="Enter new category"
                        value={newCategory}
                        onChange={handleNewCategoryChange}
                        required
                        className={styles.input}
                      />
                    </div>
                  )}
                </div>
                
                <button type="submit" className={styles.submitButton}>
                  Add FAQ
                </button>
              </form>
            )}
          </div>
        )}
        
        {Object.keys(faqsByCategory).length === 0 ? (
          <p className={styles.noFaqs}>No FAQs available.</p>
        ) : (
          <div className={styles.categoriesContainer}>
            {Object.entries(faqsByCategory).map(([category, categoryFaqs]) => (
              <div key={category} className={styles.categorySection}>
                <h2 className={styles.categoryTitle}>{category}</h2>
                <div className={styles.faqList}>
                  {categoryFaqs.map(faq => (
                    <div key={faq._id} className={styles.faqItem}>
                      {editingFaq === faq._id ? (
                        <form onSubmit={(e) => handleEditFaq(e, faq._id)} className={styles.editForm}>
                          <div className={styles.formGroup}>
                            <label htmlFor={`edit-question-${faq._id}`}>Question:</label>
                            <input
                              type="text"
                              id={`edit-question-${faq._id}`}
                              name="question"
                              value={editFormData.question}
                              onChange={handleEditInputChange}
                              required
                              className={styles.input}
                            />
                          </div>
                          
                          <div className={styles.formGroup}>
                            <label htmlFor={`edit-answer-${faq._id}`}>Answer:</label>
                            <textarea
                              id={`edit-answer-${faq._id}`}
                              name="answer"
                              value={editFormData.answer}
                              onChange={handleEditInputChange}
                              required
                              className={styles.textarea}
                            />
                          </div>
                          
                          <div className={styles.formGroup}>
                            <label htmlFor={`edit-category-${faq._id}`}>Category:</label>
                            <select
                              id={`edit-category-${faq._id}`}
                              name="category"
                              value={editFormData.category}
                              onChange={handleEditCategoryChange}
                              required
                              className={styles.select}
                            >
                              {categories.map(category => (
                                <option key={category} value={category}>{category}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div className={styles.formActions}>
                            <button type="submit" className={styles.saveButton}>
                              Save
                            </button>
                            <button 
                              type="button" 
                              onClick={cancelEditing}
                              className={styles.cancelButton}
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      ) : (
                        <>
                          <div 
                            className={styles.question}
                            onClick={() => toggleFaq(faq._id)}
                          >
                            <span>{faq.question}</span>
                            <div className={styles.questionActions}>
                              {isAdmin && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEditingFaq(faq);
                                  }}
                                  className={styles.editButton}
                                  aria-label="Edit FAQ"
                                >
                                  Edit
                                </button>
                              )}
                              <span className={styles.expandIcon}>
                                {expandedFaqs[faq._id] ? '−' : '+'}
                              </span>
                            </div>
                          </div>
                          
                          {expandedFaqs[faq._id] && (
                            <div className={styles.answer}>
                              {faq.answer}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default Faqs 